<template>
  <div class="home">
    <!-- HERO -->
    <section class="hero">
      <div class="hero-glow"></div>
      <div class="hero-glow-secondary"></div>
      <div class="hero-content">
        <h1 class="hero-name">FireX</h1>
        <h2 class="hero-what">Fire Protection</h2>
        <div class="hero-meta">
          <span class="hero-location">Yucca Valley, CA</span>
          <span class="hero-divider">/</span>
          <span class="hero-license">C-16 Lic #1141817</span>
        </div>
        <p class="hero-desc">
          Inspections, installations, repairs, and 24/7 emergency service
          for the High Desert and all of California.
        </p>
        <div class="hero-actions">
          <a href="tel:7605673777" class="btn-call">760-567-3777</a>
          <button class="btn-quote" @click="open">Get a Quote</button>
        </div>
        <div class="hero-hours">
          Office Mon–Fri 8am–5pm&ensp;&bull;&ensp;After hours: <a href="tel:4423276191">442-327-6191</a>
        </div>
      </div>
    </section>

    <!-- SERVICES -->
    <section class="services-section" id="services-preview">
      <div class="services-header reveal">
        <span class="label">Services</span>
      </div>
      <div class="services-list reveal-stagger">
        <router-link
          v-for="(service, i) in services"
          :key="i"
          :to="'/Services?activeService=' + i"
          class="service-row reveal"
        >
          <span class="service-number">0{{ i + 1 }}</span>
          <div class="service-info">
            <h3 class="service-name">{{ service.name }}</h3>
            <p class="service-desc">{{ service.short }}</p>
          </div>
          <span class="service-arrow">&rarr;</span>
        </router-link>
      </div>
    </section>

    <!-- ABOUT -->
    <section class="about-section">
      <div class="about-inner">
        <span class="label reveal">About</span>
        <h2 class="about-heading reveal">
          20+ years protecting California businesses.
        </h2>
        <p class="about-text reveal">
          We provide design, installation, testing, inspection, maintenance, and
          repair for fire protection and life safety equipment. Every customer
          gets a tailored program — because we believe the difference is service.
        </p>
        <div class="about-creds reveal">
          <div class="cred">
            <span class="cred-key">C-16</span>
            <span class="cred-val">Fire Protection Contractor #1141817</span>
          </div>
          <div class="cred">
            <span class="cred-key">E</span>
            <span class="cred-val">Extinguisher License #5063</span>
          </div>
          <div class="cred">
            <span class="cred-key">24/7</span>
            <span class="cred-val">Emergency phone support</span>
          </div>
        </div>
        <router-link to="/about-us" class="about-link reveal">More about FireX &rarr;</router-link>
      </div>
    </section>

    <!-- WORK -->
    <section class="work-section" id="work">
      <div class="work-inner">
        <span class="label reveal">Our Work</span>
        <div class="work-slider reveal">
          <splide :options="{ rewind: true, gap: '1rem', perPage: 1, pagination: true, arrows: true }">
            <splide-slide>
              <img src="../assets/img/slide1.png" alt="FireX installation project" />
            </splide-slide>
            <splide-slide>
              <img src="../assets/img/slide2.png" alt="FireX service work" />
            </splide-slide>
            <splide-slide>
              <img src="../assets/img/slide3.png" alt="FireX fire protection system" />
            </splide-slide>
          </splide>
        </div>
      </div>
    </section>

    <!-- TESTIMONIALS -->
    <section class="testimonials-section" id="testimonial">
      <div class="testimonials-inner">
        <span class="label reveal">Reviews</span>
        <div class="testimonials-list">
          <div class="testimonial reveal" v-for="(t, i) in testimonials" :key="i">
            <div class="testimonial-stars">
              <span v-for="s in 5" :key="s">&#9733;</span>
            </div>
            <p class="testimonial-text">"{{ t.text }}"</p>
            <div class="testimonial-footer">
              <strong>{{ t.name }}</strong> &mdash; {{ t.location }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT ANCHOR -->
    <div id="contact"></div>
  </div>
</template>

<script>
import { Splide, SplideSlide } from "@splidejs/vue-splide";

export default {
  components: { Splide, SplideSlide },
  data() {
    return {
      services: [
        { name: "Fire Sprinkler Systems", short: "Inspections, testing (NFPA 25), repairs, new installations, underground & standpipe inspections." },
        { name: "Extinguishers & Kitchen Suppression", short: "Annual inspections, sales, kitchen hood systems — Ansul, Pyro-Chem, Amerex." },
        { name: "Life Safety Systems", short: "Emergency lighting, exit sign testing, battery backup replacements." },
        { name: "Testing Services", short: "OS&Y testing, hydrant flow testing & flushing, fire pump inspections." },
        { name: "Repairs & Code Compliance", short: "Deficiency corrections, valve replacements, leak repairs & system upgrades." },
        { name: "Compliance & Reporting", short: "AES / Compliance Engine reporting, Fire Marshal docs, insurance compliance." },
      ],
      testimonials: [
        { name: "Spencer", location: "San Diego, CA", text: "FireX provided outstanding service from start to finish. Professional, thorough, and they truly care about safety." },
        { name: "Joe", location: "Ventura, CA", text: "Reliable and knowledgeable team. They handled our sprinkler inspection quickly and were very transparent about everything." },
        { name: "Vinny", location: "Sacramento, CA", text: "Highly recommend FireX. Their expertise and attention to detail gave us complete confidence in our fire protection systems." },
      ],
    };
  },
  methods: {
    open() {
      this.$emit("showQuote", true);
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    }
  },
  mounted() {
    this.scrollToTop();
  }
};
</script>

<style>
/* ── HERO ── */
.hero {
  min-height: calc(100vh - 72px);
  display: flex;
  align-items: flex-end;
  padding: 0 clamp(2rem, 8vw, 8rem);
  padding-bottom: 6rem;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    var(--color-bg) 0%,
    #1a0f08 40%,
    #120905 70%,
    var(--color-bg) 100%
  );
}

.hero-glow {
  position: absolute;
  top: 10%;
  right: 5%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(234, 88, 12, 0.15) 0%, rgba(234, 88, 12, 0.03) 50%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  animation: glow-pulse 6s ease-in-out infinite;
}

.hero-glow-secondary {
  position: absolute;
  bottom: 20%;
  left: 10%;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.08) 0%, transparent 60%);
  border-radius: 50%;
  pointer-events: none;
  animation: glow-pulse 8s ease-in-out 2s infinite;
}

@keyframes glow-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
}

.hero-content {
  max-width: 640px;
  position: relative;
  z-index: 2;
  animation: hero-enter 0.8s cubic-bezier(0.0, 0.0, 0.2, 1) both;
}

@keyframes hero-enter {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-name {
  font-family: var(--font-display);
  font-size: clamp(4rem, 10vw, 8rem);
  font-weight: 900;
  line-height: 0.9;
  letter-spacing: -0.04em;
  color: var(--color-text);
  margin: 0;
}

.hero-what {
  font-family: var(--font-display);
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 500;
  color: var(--color-fire);
  margin: 0.25rem 0 0;
  letter-spacing: -0.01em;
}

.hero-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  font-size: 0.85rem;
  color: var(--color-text-muted);
  letter-spacing: 0.02em;
}

.hero-divider {
  opacity: 0.3;
}

.hero-desc {
  font-size: 1rem;
  line-height: 1.7;
  color: var(--color-text-muted);
  margin: 1.25rem 0 2rem;
  max-width: 480px;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.btn-call {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.1rem;
  padding: 0.75rem 2rem;
  background: var(--color-fire);
  color: white;
  border-radius: 4px;
  text-decoration: none;
  letter-spacing: -0.01em;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 0 20px rgba(234, 88, 12, 0.2);
}

.btn-call:hover {
  background: var(--color-fire-light);
  transform: translateY(-1px);
  box-shadow: 0 0 30px rgba(234, 88, 12, 0.35);
}

.btn-call:active {
  transform: translateY(0) scale(0.98);
}

.btn-quote {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.7rem 1.5rem;
  background: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, transform 0.15s;
}

.btn-quote:hover {
  border-color: var(--color-text-muted);
  background: rgba(255, 255, 255, 0.03);
}

.btn-quote:active {
  transform: scale(0.97);
}

.hero-hours {
  margin-top: 1.25rem;
  font-size: 0.8rem;
  color: var(--color-text-muted);
}

.hero-hours a {
  color: var(--color-text-muted);
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* ── LABEL ── */
.label {
  display: block;
  font-family: var(--font-display);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-fire);
  margin-bottom: 2rem;
}

/* ── SERVICES ── */
.services-section {
  padding: 6rem clamp(2rem, 8vw, 8rem);
  border-top: 1px solid var(--color-border);
  background: linear-gradient(180deg, var(--color-bg) 0%, var(--color-surface) 100%);
}

.services-list {
  max-width: 900px;
}

.service-row {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.75rem 0;
  border-bottom: 1px solid var(--color-border);
  text-decoration: none;
  color: var(--color-text);
  transition: padding-left 0.3s cubic-bezier(0.0, 0.0, 0.2, 1),
              background 0.3s;
}

.service-row:first-child {
  border-top: 1px solid var(--color-border);
}

.service-row:hover {
  padding-left: 1.5rem;
  background: linear-gradient(90deg, rgba(234, 88, 12, 0.04) 0%, transparent 60%);
}

.service-row:hover .service-arrow {
  opacity: 1;
  transform: translateX(4px);
}

.service-row:hover .service-name {
  color: var(--color-fire-light);
}

.service-number {
  font-family: var(--font-display);
  font-size: 0.75rem;
  color: var(--color-text-muted);
  opacity: 0.3;
  padding-top: 0.35rem;
  flex-shrink: 0;
  width: 2rem;
}

.service-info {
  flex: 1;
}

.service-name {
  font-family: var(--font-display);
  font-size: clamp(1.1rem, 2.5vw, 1.4rem);
  font-weight: 700;
  margin: 0 0 0.35rem;
  letter-spacing: -0.01em;
  transition: color 0.2s;
}

.service-desc {
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--color-text-muted);
  margin: 0;
  max-width: 500px;
}

.service-arrow {
  font-size: 1.25rem;
  color: var(--color-fire);
  opacity: 0;
  transition: opacity 0.2s, transform 0.3s cubic-bezier(0.0, 0.0, 0.2, 1);
  padding-top: 0.25rem;
  flex-shrink: 0;
}

/* ── ABOUT ── */
.about-section {
  padding: 6rem clamp(2rem, 8vw, 8rem);
  background: linear-gradient(180deg, var(--color-surface) 0%, #1a1210 50%, var(--color-bg) 100%);
  border-top: 1px solid var(--color-border);
}

.about-inner {
  max-width: 700px;
}

.about-heading {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin-bottom: 1.25rem;
}

.about-text {
  font-size: 1rem;
  line-height: 1.8;
  color: var(--color-text-muted);
  margin-bottom: 2rem;
}

.about-creds {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(234, 88, 12, 0.04);
  border-left: 2px solid var(--color-fire);
  border-radius: 0 4px 4px 0;
}

.cred {
  display: flex;
  align-items: baseline;
  gap: 1rem;
}

.cred-key {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--color-fire);
  min-width: 3rem;
}

.cred-val {
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.about-link {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text);
  text-decoration: none;
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 2px;
  transition: border-color 0.2s, color 0.2s;
}

.about-link:hover {
  border-color: var(--color-fire);
  color: var(--color-fire-light);
}

/* ── WORK ── */
.work-section {
  padding: 6rem clamp(2rem, 8vw, 8rem);
  border-top: 1px solid var(--color-border);
}

.work-inner {
  max-width: 800px;
}

.work-slider img {
  width: 100%;
  height: 400px;
  object-fit: contain;
  border-radius: 4px;
}

.splide__arrow svg {
  fill: var(--color-fire) !important;
}

.splide__pagination__page.is-active {
  background: var(--color-fire) !important;
}

.splide__pagination__page {
  background: var(--color-border) !important;
}

/* ── TESTIMONIALS ── */
.testimonials-section {
  padding: 6rem clamp(2rem, 8vw, 8rem);
  background: linear-gradient(180deg, var(--color-bg) 0%, var(--color-surface) 50%, var(--color-bg) 100%);
  border-top: 1px solid var(--color-border);
}

.testimonials-inner {
  max-width: 700px;
}

.testimonials-list {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.testimonial {
  padding: 2rem;
  padding-bottom: 2rem;
  border-left: 2px solid var(--color-border);
  transition: border-color 0.3s;
}

.testimonial:hover {
  border-left-color: var(--color-fire);
}

.testimonial-stars {
  color: var(--color-amber);
  font-size: 0.9rem;
  letter-spacing: 2px;
  margin-bottom: 0.75rem;
}

.testimonial-text {
  font-size: 1.05rem;
  line-height: 1.7;
  color: var(--color-text-muted);
  margin: 0 0 0.75rem;
  font-style: italic;
}

.testimonial-footer {
  font-size: 0.8rem;
  color: var(--color-text-muted);
}

.testimonial-footer strong {
  color: var(--color-text);
}

/* ── RESPONSIVE ── */
@media (max-width: 768px) {
  .hero {
    min-height: auto;
    padding: 4rem 1.5rem 4rem;
    align-items: flex-start;
  }
  .hero-glow, .hero-glow-secondary {
    display: none;
  }
  .service-row {
    gap: 1rem;
  }
  .service-number {
    display: none;
  }
  .work-slider img {
    height: 250px;
  }
}
</style>
